import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { List, X } from "@phosphor-icons/react";
import { useState } from "react";

const NavLink = ({ to, children, testId }) => {
  const { pathname } = useLocation();
  const active = pathname === to;
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`font-accent text-xs uppercase tracking-[0.2em] transition-colors ${
        active ? "text-primary" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </Link>
  );
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const initials = (user?.display_name || "?").slice(0, 1).toUpperCase();

  return (
    <header className="fixed top-0 inset-x-0 z-[110] glass border-b border-border">
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <img src="/logo-mark.png" alt="" className="h-8 w-8 object-contain mix-blend-screen" />
          <span className="font-display text-2xl tracking-tight">
            ISEE<span className="italic text-primary">FANTASY</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/explore" testId="nav-explore">Explore</NavLink>
          {user && <NavLink to="/feed" testId="nav-feed">Feed</NavLink>}
          {user && <NavLink to="/messages" testId="nav-messages">Messages</NavLink>}
          {user && user.role === "creator" && <NavLink to="/studio" testId="nav-studio">Studio</NavLink>}
          {user && user.role === "admin" && <NavLink to="/admin" testId="nav-admin">Admin</NavLink>}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-avatar" className="flex items-center gap-2 group">
                  <span className="h-9 w-9 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center font-accent font-bold text-sm overflow-hidden">
                    {user.avatar_id ? (
                      <img src={fileUrl(user.avatar_id)} alt="" className="h-full w-full object-cover" />
                    ) : initials}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="glass border-border w-52">
                <div className="px-2 py-2">
                  <p className="font-accent font-bold text-sm truncate">{user.display_name}</p>
                  <p className="text-xs text-muted-foreground">@{user.username}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(`/u/${user.username}`)}>My Profile</DropdownMenuItem>
                {user.role === "creator" && (
                  <DropdownMenuItem data-testid="menu-studio" onClick={() => navigate("/studio")}>Creator Studio</DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>Admin Console</DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")}>Settings</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }} className="text-primary">
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <button data-testid="nav-login" onClick={() => navigate("/login")}
                className="font-accent text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors">
                Log in
              </button>
              <button data-testid="nav-signup" onClick={() => navigate("/register")}
                className="px-5 py-2.5 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-xs transition-transform active:scale-95 hover:bg-[#e6004c]">
                Join Free
              </button>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className="md:hidden text-foreground" onClick={() => setOpen(!open)}>
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden glass border-t border-border px-5 py-4 flex flex-col gap-4">
          <NavLink to="/explore" testId="nav-explore-m">Explore</NavLink>
          {user && <NavLink to="/feed" testId="nav-feed-m">Feed</NavLink>}
          {user && <NavLink to="/messages" testId="nav-messages-m">Messages</NavLink>}
          {user && user.role === "creator" && <NavLink to="/studio" testId="nav-studio-m">Studio</NavLink>}
          {user ? (
            <>
              <NavLink to={`/u/${user.username}`} testId="nav-profile-m">My Profile</NavLink>
              <button data-testid="nav-logout-m" onClick={() => { logout(); navigate("/"); }} className="text-left font-accent text-xs uppercase tracking-[0.2em] text-primary">Log out</button>
            </>
          ) : (
            <div className="flex gap-3">
              <button data-testid="nav-login-m" onClick={() => navigate("/login")} className="font-accent text-xs uppercase tracking-[0.2em] text-muted-foreground">Log in</button>
              <button data-testid="nav-signup-m" onClick={() => navigate("/register")} className="px-4 py-2 bg-primary text-primary-foreground font-accent font-bold uppercase text-xs">Join</button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
